import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import Icon from 'components/icon'
import Loading from 'components/loading/loading'
import GLOBAL from 'modules/global'
import { type UserMemberDetail } from 'modules/types'
import { useGetUserDetailQuery } from 'store/userMember'

type UserDetailProps = {
  id: string | null
  open: boolean
  onClose: () => void
}

const fields: Array<{
  label: string
  key: keyof UserMemberDetail
  render?: (value: UserMemberDetail) => React.ReactNode
}> = [
  { label: 'Member ID', key: 'memberCode' },
  { label: 'City', key: 'city' },
  { label: 'User ID', key: 'userId' },
  { label: 'Province', key: 'province' },
  { label: 'Full Name', key: 'fullName' },
  // { label: 'Region Registration', key: 'regionRegisteration' },
  { label: 'Phone Number', key: 'phoneNumber' },
  { label: 'Latest Region', key: 'latestRegion' },
  { label: 'Email', key: 'email' },
  { label: 'Time Zone', key: 'timeZone' },
  { label: 'Member Tier', key: 'memberTier' },
  {
    label: 'Coins',
    key: 'coins',
    render: (u) => (
      <TextField
        label='Coins'
        multiline
        value={GLOBAL.numberFormat(u.coins || 0)}
        fullWidth
        inputProps={{ readOnly: true }}
      />
    )
  },
  { label: 'OS Name', key: 'osName' },
  { label: 'Status', key: 'status' },
  { label: 'OS Version', key: 'osVersion' },
  {
    label: 'Date of Birth',
    key: 'dateOfBirth',
    render: (u) => (
      <ReadOnlyTextField
        label='Date of Birth'
        value={GLOBAL.formatDate(u.dateOfBirth as string) || ''}
      />
    )
  },
  { label: 'App Version', key: 'appVersion' },
  { label: 'Address', key: 'address' },
  { label: 'Language', key: 'language' },
  {
    label: 'Last Log In',
    key: 'lastLogin',
    render: (u) => (
      <ReadOnlyTextField
        label='Last Log In'
        value={GLOBAL.formatDateWithTimeSecond(u.lastLogin as string) || ''}
      />
    )
  },
  { label: 'Role', key: 'role' },
  { label: 'Postal Code', key: 'postalCode' },
  { label: 'Device Manufacturer', key: 'deviceManufacturer' },
  { label: 'Device Model', key: 'device' }
]

const UserDetail = ({ id, open, onClose }: UserDetailProps) => {
  const { data, isLoading, isFetching } = useGetUserDetailQuery(id as string, {
    skip: !id || !open
  })

  return (
    <Dialog open={open} fullWidth maxWidth='md' onClose={onClose}>
      <DialogContent>
        <DialogTitle
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          <Typography variant='h6'>Detail User</Typography>
          <Box>
            <IconButton onClick={onClose}>
              <Icon icon='Close' />
            </IconButton>
          </Box>
        </DialogTitle>
        {(isLoading || isFetching) && <Loading />}

        <Grid container spacing={2} sx={{ mt: 0.5, p: 3 }}>
          {(data ? fields : []).map((f) => (
            <Grid item xs={6} key={f.key as string}>
              {f.render ? (
                f.render(data as UserMemberDetail)
              ) : (
                <TextField
                  label={f.label}
                  multiline
                  value={data ? data[f.key] : ''}
                  fullWidth
                  inputProps={{ readOnly: true }}
                />
              )}
            </Grid>
          ))}

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle Model</TableCell>
                    <TableCell>VIN</TableCell>
                    <TableCell>Registration Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.vehicles?.length
                    ? data.vehicles
                    : [{ carModelName: '-', vin: '-', registrationDate: '' }]
                  )?.map((vehicle, index) => (
                    <TableRow key={index}>
                      <TableCell>{vehicle.carModelName}</TableCell>
                      <TableCell>{vehicle.vin}</TableCell>
                      <TableCell>
                        {GLOBAL.formatDateWithTime(vehicle.registrationDate) || ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

const ReadOnlyTextField = ({ label, value }: { label: string; value: string }) => (
  <TextField label={label} multiline value={value} fullWidth inputProps={{ readOnly: true }} />
)

export default UserDetail
